import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Info, Lightbulb } from 'lucide-react';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Skeleton } from '../../ui/skeleton';
import { Switch } from '../../ui/switch';
import { useBreadcrumb } from '../../../hooks/useBreadcrumb';
import { devicesService } from '../../../services/devices';
import { climbsService } from '../../../services/climbs';
import { gymsService } from '../../../services/gyms';
import type { Board } from '../../../types/device';
import type { Gym } from '../../../types/gym';
import type { BoardClimb } from '../../../types/climb';
import BoardGridCanvas from '../board-grid/BoardGridCanvas';
import { cellName } from '../board-grid/useCellName';
import { useBoardGrid } from '../board-grid/useBoardGrid';
import {
  ROLE_CYCLE,
  boardHoldRole,
  holdHands,
  otherHand,
  startingHandOf,
  swapHand,
} from '../board-grid/roles';
import { circuitHandIndex } from '../circuit';
import type { HoldHandControl } from './HoldHandButton';
import { isShapedWall } from '../board-grid/geometry';
import { CircuitOverlayCorner } from '../CircuitOverlayButtons';
import { CircuitOverlayPanel } from './CircuitOverlayPanel';
import { PhotoOverlayButton } from '../photo-editor/PhotoOverlayTools';
import {
  INCLINATION_STEPS,
  useBoardClimbDraft,
} from '../board-grid/useBoardClimbDraft';
import { BoardHoldBar } from './BoardHoldBar';
import { ClimbEditorFab } from './ClimbEditorFab';
import { ClimbEditorSplit } from './ClimbEditorSplit';
import { ClimbSavedDialog } from './ClimbSavedDialog';
import { MissingHint } from './MissingHint';
import { PhotoExpandButton } from './PhotoExpandButton';
import { editorExpandedHeight, editorPhotoMaxHeight } from './photo-height';
import { useEditorSplit } from './useEditorSplit';
import { useExpandablePhoto } from './useExpandablePhoto';
import {
  climbRequirements,
  requirementAnchor,
  requirementField,
  scrollToFirstRequirement,
  shortRequirementKey,
  type ClimbRequirementField,
} from './requirements';
import { DRAWING_SURFACE_ATTRIBUTE, useClearSelectionOutside } from './useClearSelectionOutside';
import { HoldRolesLegendDialog } from '../HoldRolesLegendDialog';
import { OVERLAY_ATTRIBUTE } from '../board-grid/useZoomPan';
import type { ClimbEditorRoute } from './useClimbEditorRoute';
import { NameSuggestionsDialog } from './NameSuggestionsDialog';
import { useGymNameSuggestions } from '../../../hooks/useGymNameSuggestions';
import { ValidatedTitleInput } from '../../climb-create/ValidatedTitleInput';
import WheelPickerField from '../WheelPickerField';
import type { WheelPickerOption } from '../../ui/wheel-picker';

const INCLINATION_OPTIONS: WheelPickerOption<number>[] = INCLINATION_STEPS.map((degrees) => ({
  value: degrees,
  label: `${degrees}°`,
}));

/**
 * A problem on a board: the editor's three blocks with the wall in the middle.
 *
 * The photo is the board's own — inherited and fixed, so there is nothing here
 * to pick or to crop — and the climb is painted on the grid drawn over it rather
 * than circled freehand. Everything else is the editor's: the sticky bar of
 * controls, the fields that gate the save, the one button in the corner.
 *
 * `?variantOf=<slug>` cuts an inclination variant of an existing problem: the
 * name and holds are carried over and locked, and only the grade and the angle
 * can be changed.
 */
export function BoardClimbEditor({ route }: { route: ClimbEditorRoute }) {
  const { locationSlug: gymSlug, boardId, climbSlug, variantOf, fromObserved } = route;
  const isVariant = !!variantOf;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [board, setBoard] = useState<Board | null>(null);
  const [gym, setGym] = useState<Gym | null>(null);
  const [loading, setLoading] = useState(true);
  // The problem being corrected, once it has been fetched. Absent when putting a
  // new one up, which is what tells the two apart everywhere below.
  const [editing, setEditing] = useState<BoardClimb | null>(null);
  // The climb this page has just created, which is what the optional fields are
  // then written to.
  const [saved, setSaved] = useState<{ id: string; slug: string } | null>(null);
  const [savingExtras, setSavingExtras] = useState(false);
  const [managingNames, setManagingNames] = useState(false);
  // What the colours on the wall mean, asked from the corner of the wall — see
  // the (i) in the overlay below.
  const [showLegend, setShowLegend] = useState(false);
  // The circuit's own list, open over the wall. Shut until it is asked for: the
  // run is painted on the board, and this is for reading it back.
  const [circuitPanelOpen, setCircuitPanelOpen] = useState(false);

  // The gym's queue of names, the same one the scans page spends — see
  // useGymNameSuggestions for why it is the gym's and not the browser's.
  const nameSuggestions = useGymNameSuggestions(gymSlug);

  const draft = useBoardClimbDraft({
    gymSlug,
    boardId,
    board,
    editing: editing
      ? {
          id: editing.id,
          name: editing.name ?? '',
          description: editing.description ?? null,
          gradeId: editing.grade_id ?? null,
          inclination: editing.inclination ?? null,
          circuit: !!editing.circuit,
          forcedHands: !!editing.forced_hands,
          holds: editing.board_holds ?? [],
        }
      : null,
  });
  const {
    name,
    setName,
    gradeId,
    setGradeId,
    description,
    setDescription,
    holds,
    setHolds,
    circuit,
    setCircuit,
    forcedHands,
    setForcedHands,
    setStartingHand,
    focusedHold,
    setFocusedHold,
    inclination,
    pickInclination,
    inclinationIsDefault,
    setInclinationIsDefault,
    syncInclinationDefault,
    livePreview,
    setLivePreview,
    saving,
    grid,
    adjustableInclination,
    grades,
    reset,
  } = draft;

  const { imageUrl, rows, columns, calibration, boardHolds, extraHolds, holdRadii, hasGrid } = grid;
  const { disabledCells, shapes, scanned } = grid;

  // Whether there is a wall here to paint on at all. A variant carries the
  // source problem's holds and only re-angles it, so it has a grid to look at
  // but nothing to change on it.
  const paintable = hasGrid && !!imageUrl && !isVariant;

  // The wall and the row of controls over it are one state held apart from where
  // each is drawn — here the controls are sticky at the top of the page and the
  // board is the block under them, so the two are not composed by
  // BoardGridSelector but placed one at a time.
  const boardGrid = useBoardGrid({
    rows,
    columns,
    calibration,
    holds: boardHolds,
    extraHolds,
    disabledCells,
    shapes,
    scanned,
    holdRadii,
    value: holds,
    onChange: setHolds,
    circuit,
    focusedHold,
    onFocusHold: setFocusedHold,
    // A tap places the hold and picks it up; what it *is* is answered on the
    // bar along the bottom of the wall — or armed there first. See BoardHoldBar.
    selectable: true,
  });

  // Pressing away from the wall puts the hold down: the setter has moved on to
  // the name or the grade, and the panel has nothing left to act on.
  useClearSelectionOutside(boardGrid.clearSelection);

  // A phone held sideways lays the wall and the fields side by side — once there
  // is a wall to lay out. See ClimbEditorSplit.
  const split = useEditorSplit() && hasGrid && !!imageUrl;

  // A landscape board — a spray wall shot sideways — opened out to the height of
  // a phone's screen. See useExpandablePhoto. Only where there is a wall to paint
  // on: the button sits with the rest of what is on it. Side by side it is open
  // for good, and fills the column it is given.
  const expansion = useExpandablePhoto(
    paintable ? boardGrid.aspect : null,
    boardGrid.zoom,
    split
  );

  /**
   * The hand the hold in hand is taken with, and the swap, for the bar — on a
   * circuit that forces hands. Read off the draft's run (`holds`), the one with
   * the hands on.
   *
   * The first real move has no swap of its own — its hand *is* the starting
   * hand — so there the press flips the run from the start, as the badge on the
   * list does. Everywhere else it is a swap against the alternation, written
   * onto the hold and worked out again from there (see circuit.ts).
   */
  const handControl = ((): HoldHandControl | undefined => {
    if (!circuit || !forcedHands) return undefined;

    const at = boardGrid.selectedIndex;
    const hold = at === null ? undefined : holds[at];
    return {
      hands: hold ? holdHands(hold) : [],
      onSwap: () => {
        if (at === null || !hold) return;
        if (circuitHandIndex(holds, boardHoldRole) === at) {
          return setStartingHand(otherHand(hold.hand ?? 'left'));
        }
        setHolds(swapHand(holds, at));
      },
    };
  })();

  /** Where a hold sits on the wall, for the rows of the circuit's list. */
  const holdName = (hold: { row: number; col: number }) =>
    cellName(t, hold, isShapedWall(shapes));

  useEffect(() => {
    if (!gymSlug || !boardId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      devicesService.listGymBoards(gymSlug),
      // The problem this page opens on: the one being corrected, or the one a
      // variant is being cut from. Never both.
      climbSlug || variantOf
        ? climbsService.getClimbBySlug((climbSlug ?? variantOf) as string)
        : Promise.resolve(null),
      // Only feeds the breadcrumb's logo, so a failure here must not cost the
      // setter the form.
      gymsService.getGymBySlug(gymSlug).catch(() => null),
    ])
      .then(([boards, source, loadedGym]) => {
        if (cancelled) return;
        setBoard(boards.find((b) => b.id === boardId) ?? null);
        setGym(loadedGym);
        if (!source || source.type !== 'BoardClimb') return;

        // An edit opens on the problem itself and hands it to the draft, which
        // knows how to write it back.
        if (climbSlug) return setEditing(source);

        // A variant carries the source problem's name and holds. They arrive in
        // their stored order, so the circuit does too — and with it the hands it
        // forces, which are that order read one step further.
        setName(source.name ?? '');
        setHolds(source.board_holds ?? []);
        setCircuit(!!source.circuit);
        setForcedHands(!!source.forced_hands);
        setStartingHand(startingHandOf(source.board_holds ?? []));
      })
      .catch(() => {
        if (!cancelled) setBoard(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    gymSlug,
    boardId,
    climbSlug,
    variantOf,
    setName,
    setHolds,
    setCircuit,
    setForcedHands,
    setStartingHand,
  ]);

  // Opened on the problem lit on the wall right now, which is no problem of
  // ours (`?observed=1`): the editor starts from the holds the device saw come
  // on, so the climber saves what they are already looking at rather than
  // painting it again hold by hold.
  //
  // Fetched here rather than carried in the URL: the holds are the server's
  // answer to "what is on that wall", and the page has to survive a reload —
  // which, past the observation's window, means opening on an empty wall. That
  // is the honest outcome, and the setter can paint on it.
  useEffect(() => {
    if (!fromObserved || !gymSlug || !boardId || climbSlug) return;
    let cancelled = false;

    devicesService
      .getBoardObservation(gymSlug, boardId)
      .then((observation) => {
        if (cancelled || !observation || observation.holds.length === 0) return;
        // A set of holds, never a circuit: what came off the wall is which LEDs
        // are on, and LEDs have no order to read a sequence out of.
        setHolds(observation.holds);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [fromObserved, gymSlug, boardId, climbSlug, setHolds]);

  const gradeOptions = useMemo<WheelPickerOption<string>[]>(
    () => grades.map((grade) => ({ value: grade.id, label: grade.name })),
    [grades]
  );

  // Everything the save is still waiting for. The rule lives in
  // `requirements.ts`, one call site for every kind of climb.
  const missing = useMemo(
    () => climbRequirements({ route, name, gradeId, boardHolds: holds }),
    [route, name, gradeId, holds]
  );

  // Where each of those is answered: the roles are said on the wall they are
  // painted on, the fields say it themselves — and only once the save has been
  // pressed, since a form that opens red is telling the setter off for not
  // having filled in a form they have just been handed.
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const missingFields = useMemo(
    () => new Set<ClimbRequirementField>(missing.map(requirementField)),
    [missing]
  );
  const missingOnBoard = useMemo(
    () =>
      missing
        .filter((key) => requirementField(key) === 'drawing')
        .map((key) => t(shortRequirementKey(key))),
    [missing, t]
  );
  const invalid = (field: ClimbRequirementField) => showFieldErrors && missingFields.has(field);

  /** The save pressed before its time: mark what is wanted, go to the first. */
  const showWhatIsMissing = () => {
    setShowFieldErrors(true);
    scrollToFirstRequirement([...missingFields]);
  };

  // Gym logo → board → the problem, the same trail a board climb's profile
  // shows with its own name in last place.
  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      hideHomeLogo: true,
      segments: [
        ...(gym
          ? [
              {
                label: gym.name,
                avatarUrl: gym.logo_small_url || gym.logo_url,
                onClick: () => navigate(`/gyms/${gymSlug}`),
              },
            ]
          : []),
        ...(board
          ? [
              {
                label: board.name,
                onClick: () => navigate(`/gyms/${gymSlug}?tab=board&board=${board.id}`),
              },
            ]
          : []),
        { label: climbSlug ? name : t('climbs.board.newBreadcrumb') },
      ],
      loading,
    }),
    [
      gym?.name,
      gym?.logo_small_url,
      gym?.logo_url,
      board?.id,
      board?.name,
      gymSlug,
      climbSlug,
      name,
      loading,
      navigate,
      t,
    ]
  );

  const handleSave = async () => {
    // An edit is finished when it is saved: there is nothing optional left to
    // ask for that is not already on the form.
    if (editing) {
      const updated = await draft.saveEdit();
      if (updated) navigate(-1);
      return;
    }

    const created = await draft.save();
    if (!created) return;
    // The name is spent: the next problem should not be offered the one this
    // took. A name typed by hand is not on the list and strikes nothing off.
    nameSuggestions.consume(name);
    setSaved(created);
  };

  /** Writes the fields the save did not wait for. Best-effort — the climb is up. */
  const saveExtras = async () => {
    if (!saved) return;
    setSavingExtras(true);
    try {
      await climbsService.updateClimb(saved.id, 'board_climb', {
        description: description.trim() || null,
      });
    } finally {
      setSavingExtras(false);
    }
  };

  const finishAndLeave = async () => {
    await saveExtras();
    if (!saved || !board) return;
    // Open under the board so the board's photo and calibration drive the grid.
    navigate(`/gyms/${gymSlug}/boards/${board.id}/climbs/${saved.slug}`, {
      state: { climb: saved },
    });
  };

  /** Circuit mode, with the list it opens shut behind it when it goes. */
  const toggleCircuit = (on: boolean) => {
    setCircuit(on);
    if (!on) setCircuitPanelOpen(false);
  };

  const finishAndNext = async () => {
    await saveExtras();
    setSaved(null);
    reset();
    // The draft's reset empties the problem; this one arms the legend on start
    // again. The colour in hand belongs to the legend rather than to the problem
    // — it outlives what is being painted — so clearing the holds alone would
    // leave the next problem's first tap in whatever colour this one ended on.
    boardGrid.reset();
    // And the marks that said what the last problem was missing: the next one is
    // the page opening again, not a form left half-answered.
    setShowFieldErrors(false);
    window.scrollTo({ top: 0 });
  };

  if (loading) return <Skeleton className="mx-auto mt-8 h-96 w-full max-w-xl" />;

  if (!board) {
    return (
      <div className="mx-auto mt-8 max-w-xl space-y-4 p-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 size-4" />
          {t('common.back')}
        </Button>
        <p className="text-muted-foreground">{t('climbs.board.notFound')}</p>
      </div>
    );
  }

  /** The circuit read back: over the wall upright, in the column beside it sideways. */
  const circuitPanel = (docked: boolean) => (
    <CircuitOverlayPanel
      docked={docked}
      holds={holds}
      onChange={setHolds}
      roleOf={boardHoldRole}
      nameOf={holdName}
      selected={focusedHold === null ? [] : [focusedHold]}
      onSelect={(index) => setFocusedHold(focusedHold === index ? null : index)}
      forcedHands={forcedHands}
      onForcedHandsChange={setForcedHands}
      onStartingHandChange={setStartingHand}
      onClose={() => setCircuitPanelOpen(false)}
    />
  );

  // 1. The board itself, and everything done to it — which is all on the
  // picture. Its photo is the board's own, inherited and fixed, so there
  // is nothing here to pick or to crop.
  //
  // A board photo is portrait, so left to itself it fills the screen and
  // the page reads as finished before the problem has been named: it is
  // capped to whatever is left once the first fields have their room (see
  // photo-height.ts).
  const picture =
    hasGrid && imageUrl ? (
      // The wrapper is the wall's own box — the canvas caps and centres
      // itself inside it — so what is still to be painted can be written
      // along the bottom of the picture it is painted on.
      <div
        className={expansion.expanded ? 'relative min-h-0 flex-1' : 'relative'}
        {...requirementAnchor('drawing')}
        {...{ [DRAWING_SURFACE_ATTRIBUTE]: '' }}
      >
        <BoardGridCanvas
          grid={boardGrid}
          imageUrl={imageUrl}
          maxHeight={editorPhotoMaxHeight()}
          fill={expansion.expanded}
          overlay={
            paintable ? (
              <>
                {/* Making the problem a circuit, and the way into the run it
                    describes — in the corner of the wall the order is painted
                    on. */}
                <CircuitOverlayCorner
                  circuit={{
                    on: circuit,
                    onToggle: toggleCircuit,
                    panelOpen: circuitPanelOpen,
                    onTogglePanel: setCircuitPanelOpen,
                  }}
                />

                {/* The roles, along the bottom of the wall: what the hold in
                    hand is, or what the next tap will paint — unless the
                    run's own list is open, and then that is what the setter
                    is working in: the list reaches down to where the bar
                    stands, and has its own way of picking a hold up.
                    Side by side, both stand in the column beside the wall
                    instead. */}
                {!split && !circuitPanelOpen && (
                  <BoardHoldBar grid={boardGrid} hand={handControl} />
                )}

                {/* The circuit read back, over the wall it runs up. */}
                {!split && circuitPanelOpen && circuitPanel(false)}

                {/* And what the wall is still short of, across the top of
                    it. A variant is the source problem at another angle —
                    its holds are carried over and locked — so there is
                    nothing there to be missing. */}
                <MissingHint items={missingOnBoard} />

                {/* Top-right: what is about the wall as a whole. The middle
                    of its foot is the bar's, and the bar grows to six buttons
                    on a circuit — on a short phone, where the board is capped
                    narrower than the page, a corner down there would be
                    under it. */}
                <div
                  className="absolute right-2 top-2 flex items-center gap-1"
                  {...{ [OVERLAY_ATTRIBUTE]: '' }}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  {/* Mirroring what is painted onto the wall itself: a lamp
                      that burns while it is on, so a glance says whether the
                      board is following along — only where the board can
                      actually drive its LEDs, see can_light. */}
                  {board.can_light && (
                    <PhotoOverlayButton
                      label={t('climbs.board.livePreview')}
                      title={t('climbs.board.livePreviewHint')}
                      onClick={() => setLivePreview((on) => !on)}
                      pressed={livePreview}
                    >
                      <Lightbulb className="size-4" aria-hidden />
                    </PhotoOverlayButton>
                  )}

                  {/* And what the colours mean — the one control here that
                      is worth anything before a single hold has been
                      tapped. */}
                  <button
                    type="button"
                    onClick={() => setShowLegend(true)}
                    aria-label={t('climbs.holdRoles.legend.open')}
                    title={t('climbs.holdRoles.legend.open')}
                    className="flex size-9 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur transition-colors hover:bg-black/75"
                  >
                    <Info className="size-4" aria-hidden />
                  </button>
                </div>

                {expansion.offered && <PhotoExpandButton expansion={expansion} />}
              </>
            ) : undefined
          }
        />
      </div>
    ) : (
      <p className="mx-4 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
        {t('climbs.board.missingImage')}
      </p>
    );

  // 2. What identifies the climb — the whole of what the save waits for.
  const fields = (
    <div
      className={split ? 'space-y-4 px-3 pt-2' : 'space-y-4 px-4 pt-5'}
      data-testid="climb-info-row"
    >
      {/* No label: the breadcrumb already says which problem this is, and a
          setter naming thirty of them scrolls past that row every time. The
          two buttons that do the naming sit inside the field. */}
      <div {...requirementAnchor('name')}>
        <ValidatedTitleInput
          title={name}
          locationId={null}
          boardId={board.global ? null : board.id}
          boardTypeId={board.global ? board.board_type.id : null}
          inclination={adjustableInclination ? inclination : null}
          type="board_climb"
          excludeClimbId={editing?.id ?? null}
          onTitleChange={setName}
          hasError={invalid('name')}
          errorMessage={t('climbEditor.missing.field', { item: t('climbEditor.missing.name') })}
          compactSuggestions
          suggestions={
            isVariant
              ? undefined
              : { names: nameSuggestions.names, onManage: () => setManagingNames(true) }
          }
        />
      </div>

      {/* Side by side the column is narrow, and the two stand one under the
          other rather than squeezing each other's labels onto three lines. */}
      <div
        className={`flex gap-4 border-t pt-4 text-sm ${
          split ? 'flex-col items-start' : 'items-center justify-between'
        }`}
      >
        <div {...requirementAnchor('grade')}>
          <WheelPickerField
            label={t('climbs.board.fields.grade')}
            placeholder={t('climbs.board.fields.gradePlaceholder')}
            value={gradeId || null}
            options={gradeOptions}
            onChange={setGradeId}
            invalid={invalid('grade')}
            testId="climb-grade-picker"
          />
        </div>

        {/* A board that cannot be re-angled still says what angle it hangs
            at, but there is nothing to pick. */}
        {adjustableInclination ? (
          <WheelPickerField
            label={t('climbs.board.fields.inclination')}
            value={inclination}
            options={INCLINATION_OPTIONS}
            onChange={pickInclination}
            onOpen={syncInclinationDefault}
            testId="climb-inclination-picker"
            extra={
              <div className="flex items-center gap-2">
                <Switch
                  id="board-default-inclination"
                  checked={inclinationIsDefault}
                  onCheckedChange={setInclinationIsDefault}
                  aria-label={t('climbs.board.fields.makeDefaultInclination')}
                />
                <Label
                  htmlFor="board-default-inclination"
                  className="text-sm text-muted-foreground"
                >
                  {t('climbs.board.fields.makeDefaultInclination')}
                </Label>
              </div>
            }
          />
        ) : (
          board.inclination != null && (
            <span className="text-xs text-muted-foreground">
              {t('climbs.board.fields.fixedInclination', { degrees: board.inclination })}
            </span>
          )
        )}
      </div>

      {/* An edit has the description in front of it: there is no "after the
          save" for a problem that is already up. */}
      {editing && (
        <div className="space-y-2 border-t pt-4">
          <Label htmlFor="board-climb-description">
            {t('climbs.board.fields.description')}
          </Label>
          <Textarea
            id="board-climb-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
      )}
    </div>
  );

  return (
    // Two elements because the inner one breaks out of main's gutter and
    // `mx-auto` cannot survive an `-mx`: the board photo runs edge to edge (which
    // is why everything else re-adds `px-4` for itself), and it only reached the
    // form's width while the page stayed inside that padding.
    //
    // `-mt-3` takes the row of padding above with it, so the sticky bar of
    // controls begins exactly where the navbar ends — it sticks at 65px, and a
    // band of page above it would be 12px the wall could have had.
    //
    // Opened out, the inner one is a column of the screen's height: the fields
    // take what they need and the board the rest (see editorExpandedHeight).
    <div className="mx-auto max-w-xl">
      {split ? (
        <ClimbEditorSplit
          aspect={boardGrid.aspect}
          title={
            isVariant
              ? t('climbs.board.variantTitle', { name })
              : (editing && name) || board.name
          }
          picture={picture}
          controls={
            paintable &&
            (circuitPanelOpen ? (
              circuitPanel(true)
            ) : (
              <BoardHoldBar grid={boardGrid} hand={handControl} docked={false} />
            ))
          }
        >
          {isVariant && (
            <p className="px-3 text-sm text-muted-foreground">{t('climbs.board.variantHint')}</p>
          )}
          {fields}
        </ClimbEditorSplit>
      ) : (
        <div
          className={`-mx-4 -mt-3 sm:-mx-6 ${expansion.expanded ? 'flex flex-col' : 'pb-28'}`}
          style={expansion.expanded ? { height: editorExpandedHeight() } : undefined}
        >
          {/* A variant is the source problem re-cut at another angle: its holds are
              carried over and locked, so it says which problem it is instead of
              offering anything to paint. */}
          {isVariant && (
            <div className="border-b bg-background px-4 py-2">
              <h1 className="text-xl font-semibold">{t('climbs.board.variantTitle', { name })}</h1>
              <p className="text-sm text-muted-foreground">{t('climbs.board.variantHint')}</p>
            </div>
          )}

          {picture}

          {fields}
        </div>
      )}

      <ClimbEditorFab
        ready={missing.length === 0}
        saving={saving}
        onSave={handleSave}
        onIncomplete={showWhatIsMissing}
        label={editing ? t('common.save') : t('climbEditor.save')}
      />

      <HoldRolesLegendDialog
        open={showLegend}
        onOpenChange={setShowLegend}
        roles={ROLE_CYCLE}
        hint={t('climbs.board.gridHint')}
      />

      <ClimbSavedDialog
        open={!!saved}
        onOpenChange={(open) => !open && finishAndLeave()}
        saving={savingExtras}
        onSave={finishAndLeave}
        onSaveAndNext={finishAndNext}
        nextLabel={t('climbEditor.saved.another')}
      >
        <div className="space-y-2">
          <Label htmlFor="saved-climb-description">{t('climbs.board.fields.description')}</Label>
          <Textarea
            id="saved-climb-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
      </ClimbSavedDialog>

      <NameSuggestionsDialog
        open={managingNames}
        onOpenChange={setManagingNames}
        text={nameSuggestions.text}
        onSave={nameSuggestions.save}
        count={nameSuggestions.names.length}
      />
    </div>
  );
}
